/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Address,
  NullPromise,
  QCQuoteValues,
  QuoteFrameBuyInItem,
  V6QuoteAnnotation
} from '../../../webmodule-common/interop/webmodule-interop.js';
import {
  BinaryDataWrapper,
  DealerQuotePriceAdjustmentMethod,
  ExternalProviderDefinition,
  InitialPriceDefinition,
  InlineDocument,
  InputCreateQuoteItem,
  InputQuoteState,
  InputUpdateQuoteItem,
  InputUpdateQuoteItems,
  InputUpdateQuotePrice,
  InputUpdateQuoteSetState,
  InputUpdateQuoteStateChangeReason,
  Quote,
  QuoteFinalStateReason,
  QuoteItem,
  QuoteItemBuyInData,
  QuoteItemPrice,
  QuoteItemPriceBreakdown,
  QuoteItemProviderData,
  QuoteItemRowAction,
  QuotePresentation,
  QuotePrice,
  QuotePriceCalculation,
  QuoteSetSibling,
  QuoteState,
  ResultFullQuotePrice,
  ResultGetQuote,
  ResultQuoteItem,
  UpdateQuoteItem
} from '../../api/dealer-api-interface-quote.js';
import { BlobApi } from '../../api/blob-api.js';
import {
  canAppearOnSupplierOrder,
  getQuoteNumberSuffix,
  isAnyFreehand,
  isDealerQuotePriceAdjustment,
  isFrame,
  isShipping,
  isSSI,
  mustAppearOnPurchaseOrder
} from './quote-helper-functions.js';
import { clone, compare, compareAnalyze } from '../../../webmodule-common/other/clone.js';
import {
  createQuoteProviderData,
  QuoteProviderData,
  validateAndUpgradeQuoteProviderData
} from './quote-provider-data.js';
import { DevelopmentError, showDevelopmentError } from '../../../webmodule-common/other/development-error.js';
import { EventNotify } from '../../../webmodule-common/other/ui/events.js';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away.js';
import { flagInSet, isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions.js';
import { fromJsonStr, toJsonStr } from '../../../webmodule-common/other/blob-converters.js';
import { getQuoteSupplierDisplayName, quoteSupplierProvider } from './quoteSupplierProvider.js';
import { information } from '../../../webmodule-common/other/ui/modal-option.js';
import { InformationDispatcher } from '../../../webmodule-common/other/ui/information-dispatcher.js';
import { isAutoSaving, saveWithIndicator, SaveWorkflowModal } from '../../../webmodule-common/other/save-workflow.js';
import { isSupplierUsingSSI } from './v6/helper-functions.js';
import { LangQuoteContainer } from '../../../localization/scripts/quotes/quote-container-lang';
import { localDateTimeToServer } from '../../../webmodule-common/other/datetime-converter.js';
import { LockResourceResult } from '../../../webmodule-common/other/resource-lock-info.js';
import { money } from '../../../webmodule-common/other/currency-formatter.js';
import { newGuid } from '../../../webmodule-common/other/api/guid.js';
import { PricingModel, pricingModelType } from '../../../webmodule-common/pricing/pricing-model.js';
import { QuoteApi } from '../../api/quote-api.js';
import { QuoteItemContainer } from './quote-item-container.js';
import { QuoteTechnicalReviewDialog } from './quote-technical-review-confirmation.js';
import { runEventNotify } from '../../../webmodule-common/other/array-helper.js';
import { SSIProcessor } from './ssi-processor.js';
import { StockLookupView } from '../../api/dealer-api-interface-franchisee.js';
import { v6ConfigSupplierOptions, v6SupportsVersion, v6VersionMap } from '../../v6config/v6config';

class MockSSIProcessor implements SSIProcessor {
  async processSSI(): Promise<InputUpdateQuoteItems | undefined> {
    return undefined;
  }
}

export interface ValidationDetails {
  code: string;
  message: string;
}

export interface PriceValidation {
  id: string;
  details: ValidationDetails[];
}

export interface StockLookupViewExtra extends StockLookupView {
  lookup: string;
  marginPercentage: number | null;
  calculatedGross: number | null;
  // description: string;
}

export interface ParamCreateQuoteItem {
  id: string | null;
  title: string | null;
  description: string | null;
  quantity: number | null;
  comment: string | null;
  commentToManufacturer: string | null;
  externalProvider: ExternalProviderDefinition | null;
  buyInData: BinaryDataWrapper | null;
  quoteItemContentType: number;
  thumbnail: string | null;
  price: InitialPriceDefinition | null;
  isRestrictedToPowerUser: boolean | null;
  isTaxableItem: boolean | null;
  thumbnailExtension?: string;
  inlineDocuments: InlineDocument[] | null;
}

let _num = 1;
export class QuoteContainer {
  _internalId: number = _num++;
  quoteId: string;
  quote: Quote | null;
  quotePrice: QuotePrice | null;
  private _quoteProviderData: QuoteProviderData | null;
  get quoteProviderData(): QuoteProviderData | null {
    return this._quoteProviderData;
  }
  set quoteProviderData(value: QuoteProviderData | null) {
    this._quoteProviderData = value;
  }
  quotePresentation: QuotePresentation | null = null;
  siblings: QuoteSetSibling[] | null = null;
  items: QuoteItem[] | null;
  itemsData: QuoteItemProviderData[] | null;
  itemsBuyInData: QuoteItemBuyInData[] | null;
  itemPrices: QuoteItemPrice[] | null;
  isNewQuote: boolean;

  constructor(
    quoteId: string,
    quote: Quote | null,
    quotePrice: QuotePrice | null,
    quotePresentation: QuotePresentation | null,
    siblings: QuoteSetSibling[] | null,
    items: QuoteItem[] | null,
    itemsData: QuoteItemProviderData[] | null,
    itemsBuyInData: QuoteItemBuyInData[] | null,
    itemPrices: QuoteItemPrice[] | null,
    quoteProviderData: QuoteProviderData | null = null,
    isNewQuote = false
  ) {
    this.quote = quote;
    this.quotePrice = quotePrice;
    this.quotePresentation = quotePresentation;
    this.siblings = siblings;
    this.items = items;
    this.itemsData = itemsData;
    this.itemsBuyInData = itemsBuyInData;
    this.itemPrices = itemPrices;
    this.quoteId = quoteId;
    this.quoteProviderData = quoteProviderData;
    this.isNewQuote = isNewQuote;
  }

  reset() {
    this.quote = null;
    this.quotePrice = null;
    this.quotePresentation = null;
    this.siblings = null;
    this.items = null;
    this.itemsData = null;
    this.itemsBuyInData = null;
    this.itemPrices = null;

    this.quoteProviderData = null;
  }
}

export interface IQuoteValidator {
  valid: (state: QuoteState) => Promise<boolean>;
}

// a manager that is used to find and collect quote item information
// and keep a comparison backup
export class QuoteContainerManager {
  backup: QuoteContainer;
  container: QuoteContainer;
  api: QuoteApi;
  afterSave: EventNotify[] = [];
  blobApi: BlobApi;
  numberSeed: string | null = null;
  lockInfo?: LockResourceResult;
  priceValidation: PriceValidation[] | null = null;
  quoteValidator?: IQuoteValidator;
  public forceLockout = false;
  public pricingModel: PricingModel = new PricingModel(pricingModelType.margin, 0, 99);
  protected stateChangeReason: InputUpdateQuoteStateChangeReason | null = null;
  /**
   * create and return a container for quote item data references
   * @param quoteItemId the quote item to return
   * @param useBackup if true, return the backup copy, if false the working copy
   * @returns a quoteItemContainer with the data parts combined
   */
  private itemContainerCache: QuoteItemContainer[] = [];
  private itemContainerCacheBak: QuoteItemContainer[] = [];

  constructor(original: QuoteContainer, quoteApi: QuoteApi, blobApi: BlobApi) {
    this.api = quoteApi;
    this.blobApi = blobApi;
    if (original.quote && original.quote.id !== original.quoteId)
      throw new Error(LangQuoteContainer.invalidArgumentQuoteId()); // Localized
    this.container = original;
    this.backup = this.clone(this.container);
  }

  get isV6(): boolean {
    return this.container.quote?.serviceProvider === quoteSupplierProvider.v6;
  }

  public get quoteState(): QuoteState {
    if (globalThis?.dealerQuoteState !== undefined) return globalThis?.dealerQuoteState;
    return this.quote.state;
  }

  public set quoteState(value: QuoteState) {
    this.quote.state = value;
  }

  get siblings() {
    return this.container.siblings;
  }

  hasBreakdowns(id: string) {
    const price = this.container.itemPrices?.find(x => x.id === id);
    return price?.breakdowns && price?.breakdowns.length > 0;
  }

  get isSSINeedsShippingAddress(): boolean {
    try {
      if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
        return false;
      }

      if (!this.quote?.supplierId) {
        return false;
      }

      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      const customOptions = supplierOptions?.customOptions;

      if (!customOptions || Object.keys(customOptions).length === 0) {
        return false;
      }

      const featureFlag = 'quote.ssi.trigger.shipping';
      if (!(featureFlag in customOptions)) {
        return false;
      }

      return customOptions[featureFlag] === true || customOptions[featureFlag] === 'true';
    } catch {
      return false;
    }
  }

  //default behavour true unless disabled
  get is3dModelEnabled(): boolean {
    try {
      if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
        return false;
      }

      if (!this.quote?.supplierId) {
        return false;
      }

      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      const customOptions = supplierOptions?.customOptions;

      if (!customOptions || Object.keys(customOptions).length === 0) {
        return false;
      }

      const featureFlag = 'quote.3d.model.enabled';
      if (!(featureFlag in customOptions)) {
        return true;
      }

      return customOptions[featureFlag] === true || customOptions[featureFlag] === 'true';
    } catch {
      return true;
    }
  }

  get isPricingMetricLockedBySupplier(): boolean {
    try {
      if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
        return false;
      }

      if (!this.quote?.supplierId) {
        return false;
      }

      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      const customOptions = supplierOptions?.customOptions;

      if (!customOptions || Object.keys(customOptions).length === 0) {
        return false;
      }

      const featureFlag = 'quote.supplier-margin-markup-override.enabled';
      if (!(featureFlag in customOptions)) {
        return false;
      }

      return customOptions[featureFlag] === true || customOptions[featureFlag] === 'true';
    } catch {
      return false;
    }
  }

  public get hasFrames(): boolean {
    const items = this.container.items;
    if (!items) return false;

    for (const item of items) {
      if (isFrame(item)) return true;
    }
    return false;
  }

  /**
   * the quoteId for this managed container
   */
  get quoteId(): string {
    return this.container.quoteId;
  }

  get quoteItemPriceTotal(): number {
    return this.getQuoteItemPriceTotal();
  }

  get taxableQuoteItemPriceTotal(): number {
    return this.getTaxableQuoteItemPriceTotal();
  }

  public get quoteTitle(): string {
    if (this.quote.quoteNumber != 0)
      return LangQuoteContainer.quoteTitleNumber({
        quoteNumber: getQuoteNumberSuffix(this.quote),
        title: this.quote.title
      });
    else return LangQuoteContainer.quoteTitleDraft({ title: this.quote.title });
  }

  /**
   * returns the quote object after needsQuote is called, or throws an error if the quote is unavailable.
   */
  get quote(): Quote {
    if (!this.container.quote) {
      throw new Error(LangQuoteContainer.quoteIsNull()); // Localized
    }
    return this.container.quote;
  }

  get quoteItems(): QuoteItem[] {
    if (!this.container.items) throw new Error(LangQuoteContainer.callNeedsQuoteItemsFirst()); // Localized
    return this.container.items;
  }

  get quoteItemPrices(): QuoteItemPrice[] {
    if (!this.container.itemPrices) throw new Error(LangQuoteContainer.callNeedsQuoteItemsFirst()); // Localized
    return this.container.itemPrices;
  }

  /**
   * returns the quote price object after needsQuote is called, or throws an error if the quote price is unavailable.
   */
  get quotePrice(): QuotePrice {
    if (!this.container.quotePrice) {
      throw new Error(LangQuoteContainer.quotePriceIsNull()); // Localized
    }
    return this.container.quotePrice;
  }

  private _lastSaveSuccessful = true;

  public get lastSaveSuccessful(): boolean {
    return this._lastSaveSuccessful;
  }

  public get isSupplierQuote(): boolean {
    if (!this.container.items) throw new DevelopmentError('call needsQuoteItems first');

    const items = this.container.items.filter(item => canAppearOnSupplierOrder(item)) ?? [];

    // if we end up with free hand items and/or shipping only, we should not create a purchase order
    const shouldCreatePO = items.some(item => mustAppearOnPurchaseOrder(item));
    return shouldCreatePO;
  }

  public get isBuyInDataLocked(): boolean {
    // eslint-disable-next-line no-bitwise
    return !flagInSet(this.quoteState, QuoteState.Draft | QuoteState.Active);
  }

  private _supplierName?: string;

  get supplierName(): string {
    return this._supplierName ?? '';
  }

  get dealerPriceAdjustment(): number {
    const item = this.quoteItems.find(x => isDealerQuotePriceAdjustment(x));
    if (!item) return this.quotePrice.priceAdjustment;
    const itemprice = this.quoteItemPrices.find(x => x.id === item.id);
    return (itemprice?.calculatedGrossSellingPrice ?? 0) + this.quotePrice.priceAdjustment;
  }

  get taxRate() {
    return this.quotePrice.taxPercentage / 100;
  }

  public get allowNonTaxableItems(): boolean {
    return this.container.quote?.allowNonTaxableItems ?? false;
  }

  public static async createQuoteServiceProviderData(
    supplierType: string,
    supplierId: string,
    quoteOwnerId: string
  ): NullPromise<QuoteProviderData> {
    return await createQuoteProviderData(supplierType, supplierId, quoteOwnerId);
  }

  applySupplierMarginMarkupOverride(value: number, valueType: 'margin' | 'markup' = 'margin') {
    this.quotePrice.percentMarginOrMarkup = value;
    this.quotePrice.calculation = valueType === 'margin' ? QuotePriceCalculation.Margin : QuotePriceCalculation.Markup;
  }

  getQuoteItemPriceTotal(itemPrices?: QuoteItemPrice[]) {
    let total = 0;
    //if we dont sum this to 2dp, then the UI wont always add up if someone checking.
    //the server will be doing the real calculations anyway
    (itemPrices ?? this.container.itemPrices)?.forEach(x => (total += money(x.calculatedGrossSellingPrice, 2)));
    return total;
  }
  getQuoteItemDealerPriceAdjustmentTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => (total += money(x.priceAdjustment, 2)));
    return total;
  }
  getTaxableQuoteItemPriceTotal(itemPrices?: QuoteItemPrice[]) {
    let total = 0;
    (itemPrices ?? this.container.itemPrices)?.forEach(x => {
      if (x.isTaxableItem && this.quotePrice.taxPercentage !== 0) total += money(x.calculatedGrossSellingPrice, 2);
    });
    return total;
  }

  getNonTaxableQuoteItemPriceTotal(itemPrices?: QuoteItemPrice[]) {
    let total = 0;
    (itemPrices ?? this.container.itemPrices)?.forEach(x => {
      if (!x.isTaxableItem || this.quotePrice.taxPercentage === 0) total += money(x.calculatedGrossSellingPrice, 2);
    });
    return total;
  }

  public async updatePricingRules() {
    throw new DevelopmentError('updatePricingRules not implemented');
  }

  flushQuote() {
    this.itemContainerCache = [];
    this.itemContainerCacheBak = [];
    this.container.reset();
    this.backup = this.clone(this.container);
  }

  async getInstallationAddress(): Promise<Address | null> {
    return null;
  }

  public ssiProcessor(_itemFilter?: string[] | undefined, _extraItems?: QuoteItemContainer[]): SSIProcessor {
    return new MockSSIProcessor();
  }

  public async isQuoteValid(state: QuoteState): Promise<boolean> {
    if (!this.quoteValidator) return true;

    return await this.quoteValidator.valid(state);
  }

  public addPriceValidation(id: string, code: string, message: string) {
    this.priceValidation ??= [];

    let validation = this.priceValidation.find(x => x.id == id);

    if (!validation) {
      validation = new (class implements PriceValidation {
        details: ValidationDetails[] = [];
        id = id;
      })();

      this.priceValidation.push(validation);
    }

    if (validation.details.findIndex(x => x.code == code) < 0) validation.details.push({ code, message });
  }

  public getPriceValidation(id: string) {
    return this.priceValidation?.find(x => x.id == id);
  }

  isReadonly(): boolean {
    return this.internalIsReadonly() || this.isLockedFromUse();
  }

  isLockedFromUse(): boolean {
    return this.forceLockout || !(this.lockInfo?.isLockOwner ?? true);
  }

  /**
   *
   * @param id the quoteItemId
   * @param returnNextPositionIfNotExists
   */
  public itemPosition(id?: string, returnNextPositionIfNotExists?: boolean): number | undefined {
    if (id === undefined) return undefined;
    //TODO - implement a quote item position tracker, managed at the quote level and use this for rendering
    const index = this.container.items?.findIndex(x => x.id === id) ?? -1;
    return index === -1 && returnNextPositionIfNotExists ? (this.container.items?.length ?? 1) : index + 1;
  }

  /**
   * Simple wrapper around structuredClone
   * @param item an object of any basic type to clone
   * @returns
   */
  clone<ItemType>(item: ItemType): ItemType {
    return clone(item);
  }

  getQuoteItemExtendedListPrice(id: string, breakdown?: QuoteItemPriceBreakdown): number {
    const price = breakdown ?? this.container.itemPrices?.find(x => x.id == id);
    if (price == undefined) return 0;

    return price.supplierGrossQuantityCost;
  }

  getQuoteItemExtendedListPriceTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => {
      const item = this.getItemById(x.id);
      if (!isAnyFreehand(item)) total += money(x.supplierGrossQuantityCost, 2);
    });
    return total;
  }

  getQuoteItemMultiplier(id: string, breakdown?: QuoteItemPriceBreakdown): number {
    const price = breakdown ?? this.container.itemPrices?.find(x => x.id == id);
    if (price == undefined) return 0;

    return (
      price.supplierNettQuantityCost /
      (price.supplierGrossQuantityCost + (price.supplierGrossQuantityCost == 0 ? 1 : 0))
    );
  }

  getQuoteItemMultiplierTotal(): number {
    let supplierNettQuantityCostTotal = 0;
    let supplierGrossQuantityCostTotal = 0;

    this.container.itemPrices?.forEach(x => {
      if (!isAnyFreehand(this.getItemById(x.id))) {
        supplierNettQuantityCostTotal += money(x.supplierNettQuantityCost, 2);
        supplierGrossQuantityCostTotal += money(x.supplierGrossQuantityCost, 2);
      }
    });
    return (
      supplierNettQuantityCostTotal / (supplierGrossQuantityCostTotal + (supplierGrossQuantityCostTotal == 0 ? 1 : 0))
    );
  }

  getQuoteItemSupplierPriceAdjustment(id: string, breakdown?: QuoteItemPriceBreakdown): number {
    // Always lookup the price object for the item
    const price = this.container.itemPrices?.find(x => x.id == id);
    if (!price) return 0;
    const supplierAdjustment = price.supplierPriceAdjustmentEnabled ? price.supplierPriceAdjustment : 0;
    if (breakdown) {
      // Calculate proportional supplierPriceAdjustment for this breakdown
      const totalGross = price.supplierGrossQuantityCost;
      const breakdownGross = breakdown.supplierGrossQuantityCost ?? 0;
      const proportion = totalGross > 0 ? breakdownGross / totalGross : 0;

      const proportionalAdjustment = supplierAdjustment * proportion;
      return proportionalAdjustment;
    }

    return supplierAdjustment;
  }

  getQuoteItemSupplierPriceAdjustmentTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => {
      const supplierAdjustment = x.supplierPriceAdjustmentEnabled ? x.supplierPriceAdjustment : 0;
      if (isAnyFreehand(this.getItemById(x.id))) total += money(supplierAdjustment, 2);
    });
    return total;
  }

  getQuoteItemDealerCost(id: string, breakDown?: QuoteItemPriceBreakdown): number {
    // Always lookup the price object for the item
    const price = this.container.itemPrices?.find(x => x.id == id);
    if (!price) return 0;
    const supplierAdjustment = price.supplierPriceAdjustmentEnabled ? price.supplierPriceAdjustment : 0;
    if (breakDown) {
      // Calculate proportional priceAdjustment for this breakdown
      const totalGross = price.supplierGrossQuantityCost;
      const breakdownGross = breakDown.supplierGrossQuantityCost ?? 0;
      const proportion = totalGross > 0 ? breakdownGross / totalGross : 0;
      const proportionalAdjustment = supplierAdjustment * proportion;
      return breakdownGross + proportionalAdjustment;
    }

    return price.supplierNettQuantityCost + supplierAdjustment;
  }

  getQuoteItemDealerCostTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => {
      const supplierAdjustment = x.supplierPriceAdjustmentEnabled ? x.supplierPriceAdjustment : 0;
      total += money(x.supplierNettQuantityCost + supplierAdjustment, 2);
    });
    return total;
  }

  getQuoteItemMarginOrMarkupPercent(id: string): number {
    const price = this.container.itemPrices?.find(x => x.id == id);
    if (price == undefined) return 0;

    return price.percentMarginOrMarkup ?? this.quotePrice.percentMarginOrMarkup;
  }

  getQuoteItemDealerPriceAdjustment(id: string, breakDown?: QuoteItemPriceBreakdown): number {
    // Always lookup the price object for the item
    const price = this.container.itemPrices?.find(x => x.id == id);
    if (!price) return 0;

    if (breakDown) {
      // Calculate proportional priceAdjustment for this breakdown
      const totalGross = price.supplierGrossQuantityCost;
      const breakdownGross = breakDown.supplierGrossQuantityCost ?? 0;
      const proportion = totalGross > 0 ? breakdownGross / totalGross : 0;
      const proportionalAdjustment = price.priceAdjustment * proportion;
      return proportionalAdjustment;
    }

    return price.priceAdjustment;
  }

  quoteItemTax(id: string, breakDown?: QuoteItemPriceBreakdown): number {
    const price = this.container.itemPrices?.find(x => x.id === id);
    if (price === undefined) return 0;

    const gross = breakDown?.calculatedGrossSellingPrice ?? price.calculatedGrossSellingPrice;
    return price.isTaxableItem ? gross * this.taxRate : 0;
  }

  quoteItemPrice(id: string, removeBuyInCost = false): number {
    const price = this.container.itemPrices?.find(x => x.id == id);
    if (price == undefined) return 0;

    let linePrice = price.calculatedGrossSellingPrice;

    if (removeBuyInCost) {
      const quoteItemBuyInData = this.container.itemsBuyInData?.find(x => x.id == id);

      if (quoteItemBuyInData != undefined) {
        const buyIns = fromJsonStr<StockLookupViewExtra[]>(quoteItemBuyInData.buyInData);

        const sum =
          buyIns?.reduce((accumulator, currentObject) => {
            return accumulator + (currentObject.calculatedGross ?? 0);
          }, 0) ?? 0;

        linePrice = linePrice - sum;
      }
    }

    return linePrice;
  }

  /**
   * this will ensure at an async level that the quote propery is valid, before accessing the property synchronously
   * @returns true if the quote property is now valid
   */
  async needsQuote(): Promise<boolean> {
    if (!this.container.quote) {
      const result = await this.api.getQuote({ quoteId: this.quoteId });
      if (result) {
        await this.resetQuote(result.quote, result.quotePrice, result.quotePresentation, result.siblings);
      } else return false;
    }

    //This should only execute if the container is constructed with the quote passed in and the provider data not,
    //meaning that the quote is fetched outside the container and passed in.
    //This will fetch the data for the service provider. We are not using the value(s) from this.container.quote.serviceProvider
    //as it may be out of date.
    if (this.container.quote && !this.container.quoteProviderData) {
      this.container.quoteProviderData = await createQuoteProviderData(
        this.container.quote.serviceProvider,
        this.container.quote.supplierId,
        this.container.quote.quoteOwnerId
      );
    }
    await this.loadSupplierName();
    await this.updatePricingRules();
    return true;
  }

  async getFreshQuoteCopy(): NullPromise<ResultGetQuote> {
    return await this.api.getQuote({ quoteId: this.quoteId });
  }

  public generateQuoteNumberOnSave() {
    this.numberSeed = this.quote.quoteOwnerId;
  }

  protected async isSupplierReviewEnabled(): Promise<boolean> {
    return true;
  }

  async quoteStateChange(state: QuoteState): Promise<boolean> {
    const originalState = this.quoteState;
    const originalAllowSupplierApproval = this.quote.supplierAuthorizedToIssueOrderOnQuoteApproval;
    const seed = this.numberSeed;
    const qn = this.quote.quoteNumber;

    const restoreQuoteState = () => {
      this.quote.supplierAuthorizedToIssueOrderOnQuoteApproval = originalAllowSupplierApproval;
      this.quoteState = originalState;
      this.quote.quoteNumber = qn;
      this.numberSeed = seed;
      this.stateChangeReason = null;
    };
    const allowSupplierReview = await this.isSupplierReviewEnabled();
    const addSiblingStates = async (input: InputQuoteState[], rollback?: boolean) => {
      if (
        (state === QuoteState.Accepted && !this.quote.supplierApproved) ||
        (state === QuoteState.Approved && !allowSupplierReview)
      ) {
        //if we are here, we need to locate the rejected reason, and then reject any quotes
        //that are active or issued at the same time as accepting this quote
        const inputRejection = this.getQuoteSuperceededResult();
        this.siblings
          // eslint-disable-next-line no-bitwise
          ?.filter(x => flagInSet(x.state, QuoteState.Active | QuoteState.IssuePending | QuoteState.Issued))
          .forEach(s => {
            input.push({
              quoteId: s.quoteId,
              state: rollback ? s.state : QuoteState.Rejected,
              stateChangeReason: rollback ? null : inputRejection
            });
          });
      }
    };

    const performSave = async (rollback?: boolean) => {
      if (
        flagInSet(
          originalState,
          // eslint-disable-next-line no-bitwise
          QuoteState.Draft |
            QuoteState.Active |
            QuoteState.IssuePending |
            QuoteState.SupplierReviewPending |
            QuoteState.SupplierReviewed
        ) ||
        (originalState === QuoteState.Issued && !this.quote.supplierApproved)
      ) {
        let siblingUpdate: InputQuoteState[] | null = [];
        if (
          (this.quoteState === QuoteState.Accepted && !this.quote.supplierApproved) ||
          (this.quoteState === QuoteState.Approved && !allowSupplierReview)
        ) {
          await addSiblingStates(siblingUpdate, rollback);
          if (siblingUpdate.length === 0) siblingUpdate = null;
          if (this.isSupplierQuote && allowSupplierReview && !(await this.performSupplierMayApproveQuoteProcess()))
            return false;
        }

        //we need to use saveQuote here because the quote may have other things changed and edited.
        return await this.saveQuote(rollback, siblingUpdate);
      } else {
        const input: InputUpdateQuoteSetState = {
          quoteState: [
            {
              quoteId: this.quote.id,
              state: this.quoteState,
              stateChangeReason: null
            }
          ]
        };
        await addSiblingStates(input.quoteState, rollback);
        return await this.saveQuoteSetState(input);
      }
    };

    if (!(await this.quoteStateBeforeChange(state, originalState))) return false;

    this.quoteState = state;
    try {
      if (!(await performSave())) {
        restoreQuoteState();
        return false;
      }

      if (!(await this.quoteStateAfterChange(state, originalState))) {
        restoreQuoteState();
        await performSave(true);
        return false;
      }
    } catch (e) {
      restoreQuoteState();
      await performSave(true);
      throw e;
    }
    return true;
  }

  async performSupplierMayApproveQuoteProcess(): Promise<boolean> {
    const supplierName = this.supplierName;
    const v6Settings = v6ConfigSupplierOptions(this.quote.supplierId);
    const poNumberRequired = v6Settings.isPurchaseOrderNumberMandatory;
    const forceIssueOrder = v6Settings.isPurchaseOrderIssuedImmediately;
    const dialog = new QuoteTechnicalReviewDialog(
      poNumberRequired,
      forceIssueOrder,
      supplierName,
      this.quote.customQuoteNumber ?? ''
    );
    await dialog.showModal();
    if (!dialog.ok) return false;

    this.quote.supplierAuthorizedToIssueOrderOnQuoteApproval = dialog.allowSupplierToIssue;
    this.quote.customQuoteNumber = dialog.orderNumber;
    return true;
  }

  async saveQuoteSetState(input: InputUpdateQuoteSetState) {
    const result = await this.api.updateQuoteSetState(input);
    if (!result) return false;
    const thisState = result.items.find(x => x.quoteId === this.quote.id);
    if (!thisState) throw new DevelopmentError(LangQuoteContainer.updateQuoteStateMissingThisQuote()); // Localized
    this.quoteState === thisState.state;
    this.quote.supplierApproved = thisState.supplierApproved;
    this.quote.recordVersion = thisState.recordVersion;
    this.backup.quote = clone(this.quote);
    result.items.forEach(thisState => {
      const sib = this.siblings?.find(x => x.quoteId === thisState.quoteId);
      if (sib) {
        sib.state = thisState.state;
        sib.recordVersion = thisState.recordVersion;
        sib.supplierApproved = thisState.supplierApproved;
      }
    });
    this.backup.siblings = clone(this.siblings?.filter(x => x.quoteId !== this.quote.id) ?? null);
    await this.doAfterSave();
    return true;
  }

  getQuoteSuperceededResult(): InputUpdateQuoteStateChangeReason {
    const x: InputUpdateQuoteStateChangeReason = {
      comment: '',
      finalStateReason: QuoteFinalStateReason.Superseded
    };
    return x;
  }

  async internalSaveQuote(siblings?: InputQuoteState[] | null) {
    this.quote.serviceProviderData = toJsonStr(this.container.quoteProviderData) ?? null;
    return await this.api.updateQuote({
      siblingQuoteState: siblings ?? null,
      quote: this.quote,
      quotePrice: this.quotePrice,
      numberSeed: this.numberSeed,
      stateChangeReason: this.stateChangeReason,
      quotePresentation: this.container.quotePresentation
    });
  }

  async runPricingAdjustments(options: {
    method: DealerQuotePriceAdjustmentMethod;
    value: number;
    isTaxableAdjustment: boolean;
    percentMarginOrMarkup?: number;
    dryRun: boolean;
    quoteItems?: QuoteItem[];
    quoteItemPrices?: QuoteItemPrice[];
    quotePrice?: QuotePrice;
    adjustmentTitle?: string;
    adjustmentDescription?: string;
    adjustmentComment?: string;
  }): NullPromise<{ quotePrice: QuotePrice; itemPrices: QuoteItemPrice[]; items: QuoteItem[] }> {
    if (options.dryRun) {
      if (!options.quoteItems || !options.quotePrice || !options.quoteItemPrices)
        throw new DevelopmentError('dryRun is true, but quote data is not provided');
    } else {
      if (options.quoteItems || options.quotePrice || options.quoteItemPrices)
        throw new DevelopmentError('dryRun is false, but quote data is provided');
    }
    const quotePrice = options.quotePrice ?? this.quotePrice;
    let quoteItems = options.quoteItems ?? this.quoteItems;
    const quoteItemPrices = options.quoteItemPrices ?? this.quoteItemPrices;
    let adjustmentPriceItem = quoteItems.find(x => isDealerQuotePriceAdjustment(x));

    if (options.percentMarginOrMarkup) quotePrice.percentMarginOrMarkup = options.percentMarginOrMarkup;
    const input: InputUpdateQuotePrice = {
      quotePrice: quotePrice,
      quoteItemPrices: quoteItemPrices,
      commitChanges: !options.dryRun,
      quoteItems: quoteItems,
      dealerQuotePriceAdjustment: {
        adjustmentMethod: options.method,
        adjustmentValue: options.value,
        isTaxable: options.isTaxableAdjustment,
        title: options.adjustmentTitle ?? null,
        description: options.adjustmentDescription ?? null,
        comment: options.adjustmentComment ?? null
      }
    };
    const result = await this.api.updateQuotePrice(input);
    if (options.dryRun) {
      if (result?.resultFullQuotePrice) {
        adjustmentPriceItem = result.updatedQuoteItems.length > 0 ? result.updatedQuoteItems[0] : undefined;
        quoteItems = adjustmentPriceItem
          ? [...(quoteItems?.filter(x => !isDealerQuotePriceAdjustment(x)) ?? []), adjustmentPriceItem]
          : quoteItems?.filter(x => !isDealerQuotePriceAdjustment(x));

        if (result.resultFullQuotePrice.itemPrices.some(x => !quoteItems.find(y => y.id === x.id))) {
          await information('Bad Match');
          console.log('sds');
        }
        return {
          quotePrice: result.resultFullQuotePrice.quotePrice,
          itemPrices: result.resultFullQuotePrice.itemPrices,
          items: quoteItems ?? []
        };
      }
      return null;
    } else if (result) {
      if (result.updatedQuoteItems.length === 0) {
        const adjustmentPriceItem = this.quoteItems.find(x => isDealerQuotePriceAdjustment(x));
        if (adjustmentPriceItem) this.internalDeleteQuoteItemReference(adjustmentPriceItem.id);
      } else {
        const adjustmentPriceItem = result.updatedQuoteItems[0];
        const adjustmentPrice =
          result.resultFullQuotePrice?.itemPrices.find(x => x.id === adjustmentPriceItem.id) ?? null;

        await this.processResultItem({
          quoteItem: adjustmentPriceItem,
          quoteItemPrice: adjustmentPrice,
          quoteItemBuyInData: null,
          quoteItemProviderData: null
        });
        this.performItemSorting();
      }

      await this.resetQuote(
        result.quote!, //never null when the input was not null
        result.resultFullQuotePrice!.quotePrice,
        result.quotePresentation,
        result.siblings //as above
      );

      this.updateItemPrices(result.resultFullQuotePrice!.itemPrices);
      this.updateItemBuyIns(result.resultFullQuotePrice!.itemBuyIns);
      await this.validateBuyInPrices();

      fireQuickSuccessToast(LangQuoteContainer.quoteSaved({ title: this.quoteTitle }));
      await this.doAfterSave();

      return {
        quotePrice: this.quotePrice,
        itemPrices: this.quoteItemPrices,
        items: this.quoteItems
      };
    } else return null;
  }

  /**
   * this is called to send the current quote and quote price objects to update the server.
   * on sucessful update, the internal quote and price objects will be replaced with the new copies
   * from the server
   * when updating prices we also refresh all pricing for all items at the same time that may have been
   * reliant on the pricing.
   * calling this may result in some quoteItemContainers holding outdated objects that need replacement
   */
  async saveQuote(silently?: boolean, siblings?: InputQuoteState[] | null): Promise<boolean> {
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return false;
    }
    const result = await this.internalSaveQuote(siblings);

    if (result) {
      //quote update doesn't alter presentation, so we keep what we have
      await this.resetQuote(
        result.quote!, //never null when the input was not null
        result.resultFullQuotePrice!.quotePrice!,
        result.quotePresentation,
        result.siblings! //as above
      );
      this.updateItemPrices(result.resultFullQuotePrice!.itemPrices);
      this.updateItemBuyIns(result.resultFullQuotePrice!.itemBuyIns);
      await this.validateBuyInPrices();

      if (!silently) fireQuickSuccessToast(LangQuoteContainer.quoteSaved({ title: this.quoteTitle }));
      await this.doAfterSave();
      ``;
      return true;
    }
    return false;
  }

  /**
   * this will find the backup of a quote item and related data, and replace the items with a clone
   * of the backup. this will make any quoteItemContainers to this id stale and need replacing
   *
   * @param id the quote item id to replace
   * @returns a reference to the fresh copy of the data
   */
  restoreQuoteItemFromBackup(id: string | undefined): QuoteItemContainer | null {
    if (!id) return null;
    const backup = this.quoteItemContainer(id, true);
    this.replaceQuoteItem(backup, false);
    this.removeContainerCache(id);
    return this.quoteItemContainer(id);
  }

  changedItem(id: string | undefined): boolean {
    function comparable(qic: QuoteItemContainer) {
      const { clientSideCache, ...backup } = qic;
      return backup;
    }

    if (!id) return false;
    const backup = comparable(this.quoteItemContainer(id, true));
    const current = comparable(this.quoteItemContainer(id, false));

    const changed = !compare(backup, current);
    if (changed && globalThis?.isDevelopmentMode) {
      const backupFrame = JSON.parse(backup.data?.providerData ?? '{}');
      const currentFrame = JSON.parse(current.data?.providerData ?? '{}');
      console.debug('Quote item changed');
      console.debug('Frame Changes');
      console.debug(compareAnalyze(backupFrame, currentFrame));
      console.debug('Price Changes');
      console.debug(compareAnalyze(backup.price, current.price));
      console.debug('Item Changes');
      console.debug(compareAnalyze(backup.item, current.item));

      console.debug(backupFrame, currentFrame);
    }
    return changed;
  }

  changedItemQuantity(id: string | undefined): boolean {
    if (!id) return false;
    const backup = this.quoteItemContainer(id, true);
    const current = this.quoteItemContainer(id, false);

    return backup.item.quantity !== current.item.quantity;
  }

  async performItemSorting() {
    if (!this.container.items) await this.needsQuoteItems();
    else this.container.items = await this.internalSortItemsAsList();
  }

  /**
   * Updates the sort order of items in the container's quote presentation.
   *
   * @param {string[]} sortedOrder - The new sorted order of items.
   * @returns {Promise<boolean>} - A promise that resolves to true if the sort order was updated successfully, and false otherwise.
   */
  async updateItemSortOrder(sortedOrder: string[]) {
    if (!this.container.quotePresentation) return false;

    if (this.container.quotePresentation.itemDisplayOrder.length !== sortedOrder.length) return false;

    const sorted1 = this.container.quotePresentation.itemDisplayOrder.slice().sort();
    const sorted2 = sortedOrder.slice().sort();

    for (let i = 0; i < sorted1.length; i++) {
      if (sorted1[i] !== sorted2[i]) {
        return false;
      }
    }

    this.container.quotePresentation.itemDisplayOrder = sortedOrder;
    const saved = await this.saveQuote();
    if (saved) await this.performItemSorting();
    return saved;
  }
  quoteAttributesRequireSSIRecalculation(): boolean {
    return false;
  }
  async anyFrameOutOfDate(_errors: string[]): Promise<boolean> {
    return false;
  }

  updateQuoteTermsAndConditions(terms: string | null | undefined) {
    if (this.container.quote) this.container.quote.termsAndConditions = terms ?? null;
  }

  /**
   * this should be called any time before accessing the quote items list, which may yet be unpopulated.
   * calling this makes all references to data invalid so it should not be called while there are active
   * items being used or edited.
   * @param forceRefresh force a reload of the data even if not required.
   * @returns true if suceeded
   */
  async needsQuoteItems(forceRefresh = false): Promise<boolean> {
    if (!this.container.items || forceRefresh) {
      this.itemContainerCache = [];
      this.itemContainerCacheBak = [];
      const result = await this.api.getQuoteItemsSummary({
        quoteId: this.quoteId,
        includeProviderData: true
      });
      if (result) {
        const sortedItems: QuoteItem[] = await this.internalSortItemsAsList(result.items);

        this.container.items = sortedItems;
        this.container.itemsData = result.data;
        this.container.itemsBuyInData = result.quoteItemBuyInData;
        this.container.itemPrices = result.prices;

        await this.validateBuyInPrices();

        this.backup.items = this.clone(sortedItems);
        this.backup.itemsData = this.clone(result.data);
        this.backup.itemsBuyInData = this.clone(result.quoteItemBuyInData);
        this.backup.itemPrices = this.clone(result.prices);
      } else return false;
    }
    return true;
  }

  saveQuoteCalculations(_v: QCQuoteValues | null | undefined) {
    //override this method to save the quote calculations
  }

  saveQuoteAnnotations(_v: V6QuoteAnnotation[] | null | undefined) {
    //override this method to save the quote annotations
  }

  quoteProviderData(): QuoteProviderData | null {
    return this.container.quoteProviderData;
  }

  quoteItemContainerCache(quoteItemId: string, useBackup = false): QuoteItemContainer {
    const cache = useBackup ? this.itemContainerCacheBak : this.itemContainerCache;
    let result = cache.find(x => x.item.id === quoteItemId);
    if (!result) {
      const container = useBackup ? this.backup : this.container;
      const item = container.items?.find(x => x.id === quoteItemId);
      const data = container.itemsData?.find(x => x.id == quoteItemId);
      const buyInData = container.itemsBuyInData?.find(x => x.id == quoteItemId);
      const price = container.itemPrices?.find(x => x.id == quoteItemId);
      if (!item || !price) throw new Error(LangQuoteContainer.invalidQuoteItemId()); // Localized
      result = {
        item: item,
        data: data ?? null,
        buyInData: buyInData?.buyInData ?? undefined,
        price: price
      };
      cache.push(result);
    }

    return result;
  }

  quoteItemContainer(quoteItemId: string, useBackup = false): QuoteItemContainer {
    return this.quoteItemContainerCache(quoteItemId, useBackup);
  }

  quoteItemContainerClone(quoteItemId: string, useBackup = false): QuoteItemContainer {
    return this.cloneItemContainer(this.quoteItemContainerCache(quoteItemId, useBackup));
  }

  /**
   * Create a new quote item and add it to this quote instance. will ensure the quote items list is valid first before adding
   *
   * @param input parameters for the new quote item
   * @returns a valid object on success
   */
  async createQuoteItem(input: ParamCreateQuoteItem): NullPromise<QuoteItemContainer> {
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return null;
    }

    const newId = input.id ?? newGuid();
    const virtualThumbnailPath = input.thumbnail
      ? this.api.createQuoteItemThumbnailPath(this.quoteId, newId, input.thumbnailExtension ?? '.svg')
      : '';
    //ensure we have a correct listing before adding more items
    await this.needsQuoteItems();
    const inputParam: InputCreateQuoteItem = {
      quoteId: this.quoteId,
      quoteItemId: newId,
      virtualThumbnailPath: virtualThumbnailPath,
      ...input
    };
    const result = await this.api.createQuoteItem(inputParam);
    if (result) {
      await this.addQuoteItem(
        result.quoteItem,
        result.quoteItemPrice,
        result.quoteItemProviderData,
        result.quoteItemBuyInData
      );

      await this.resetQuote(result.quote, result.resultFullQuotePrice!.quotePrice, result.quotePresentation, null);
      if (result.resultFullQuotePrice!.itemPrices) this.updateItemPrices(result.resultFullQuotePrice!.itemPrices);
      if (result.resultFullQuotePrice!.itemBuyIns) this.updateItemBuyIns(result.resultFullQuotePrice!.itemBuyIns);

      if (input.thumbnail) {
        this.postImageBlob('', virtualThumbnailPath, input.thumbnail);
      }
      await this.validateBuyInPricesForItem(result.quoteItem.id);
      await this.doAfterSave();
      return this.quoteItemContainer(result.quoteItem.id);
    }
    return null;
  }

  /**
   * update a quote item on the server and its thumbnail svg image
   * after update refreshes the quote and quote price object, and all item prices
   * @param quoteItemContainer
   * @param thumbnail
   * @returns the new refreshed copy for the quoteitem
   */
  async createQuoteItemEx(
    quoteItemContainer: QuoteItemContainer,
    thumbnail: string,
    extraItems?: InputUpdateQuoteItems,
    thumbnailExtension?: string
  ): Promise<QuoteItemContainer> {
    this._lastSaveSuccessful = false;

    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return quoteItemContainer;
    }

    //if thumbnail is an empty string, then we are not changing anything
    let currentImagePath = '';
    let newImagePath = '';
    if (thumbnail !== '') {
      currentImagePath = quoteItemContainer.item.virtualThumbnailPath ?? '';
      //generate a new image path to refer to when saving the quote item
      newImagePath = this.api.createQuoteItemThumbnailPath(
        this.quoteId,
        quoteItemContainer.item.id,
        thumbnailExtension ?? '.svg'
      );
      quoteItemContainer.item.virtualThumbnailPath = newImagePath;
    }

    this._lastSaveSuccessful = await this.procesItemUpdateBatch(
      quoteItemContainer,
      QuoteItemRowAction.Insert,
      extraItems,
      async () => {
        if (thumbnail !== '')
          if (isAutoSaving()) await this.postImageBlob(currentImagePath, newImagePath, thumbnail);
          else this.postImageBlob(currentImagePath, newImagePath, thumbnail);
      }
    );

    return this.quoteItemContainer(quoteItemContainer.item.id);
  }

  public async copyQuoteItem(quoteItemContainer: QuoteItemContainer): NullPromise<QuoteItemContainer> {
    let value: QuoteItemContainer | null = null;
    try {
      if (!(await this.copyQuoteItemBefore(quoteItemContainer))) return null;

      value = await this.copyQuoteItemExecute(quoteItemContainer);
      if (!value) return null;
      if (!(await this.copyQuoteItemAfter(quoteItemContainer, value))) {
        try {
          if (value) await this.deleteQuoteItem(value);
        } catch (e) {
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          throw new DevelopmentError(`trying to delete quoteitem after failed copy. ${(e as object).toString()}`);
        }
        return null;
      }
      return value;
    } finally {
      await this.copyQuoteItemFinally(quoteItemContainer, value);
    }
  }

  async processResultItem(item: ResultQuoteItem) {
    if (!item.quoteItem) return;
    const newContainer: QuoteItemContainer = {
      item: item.quoteItem,
      data: item.quoteItemProviderData,
      price: item.quoteItemPrice!,
      buyInData: item.quoteItemBuyInData ?? undefined
    };
    this.removeContainerCache(newContainer.item.id);
    if (this.hasItemById(newContainer.item.id)) {
      this.updateItemContainer(newContainer, false);
      this.updateItemContainer(newContainer, true);
    } else {
      if (item.quoteItem && item.quoteItemPrice)
        await this.addQuoteItem(
          item.quoteItem,
          item.quoteItemPrice,
          item.quoteItemProviderData,
          item.quoteItemBuyInData
        );
    }
    await this.validateBuyInPricesForItem(item.quoteItem.id);
  }

  async internalUpdateQuoteItems(primaryItem: UpdateQuoteItem | null, extraItems: InputUpdateQuoteItems) {
    //we need to save the quote first now, because there is a chance that the Quote Calculations which are
    //returned as part of ssi, but stored on the quote block storage.
    //we only want to update quote without updating any other stuff like quote items , or prices as this
    //will all be done in the stuff we are about to update
    if (this.quoteChanged()) await this.internalSaveQuote();

    const inputItems = clone(extraItems);
    if (primaryItem) inputItems.items.push(primaryItem);
    const result = await this.api.updateQuoteItems(inputItems);
    //remove deleted items from local cache
    inputItems.items.forEach(element => {
      if (element.action === QuoteItemRowAction.Delete && element.input.quoteItem)
        this.internalDeleteQuoteItemReference(element.input.quoteItem.id);
    });
    return result;
  }

  async procesItemUpdateBatch(
    quoteItemContainer: QuoteItemContainer | null,
    action: QuoteItemRowAction,
    extraItems?: InputUpdateQuoteItems,
    postUpdateEvent?: () => Promise<void>
  ) {
    const updateItemInput: InputUpdateQuoteItem | null = quoteItemContainer
      ? {
          quoteItemId: quoteItemContainer.item.id,
          ProviderDataDifference: quoteItemContainer.differenceReport
            ? toJsonStr(quoteItemContainer.differenceReport)
            : null,
          quoteItem: quoteItemContainer.item,
          quoteItemProviderData: quoteItemContainer.data,
          quoteItemBuyInData: { data: quoteItemContainer.buyInData ?? null },
          quoteItemPrice: quoteItemContainer.price
        }
      : null;

    const result = await this.internalUpdateQuoteItems(
      updateItemInput
        ? {
            action: action,
            input: updateItemInput
          }
        : null,
      extraItems ?? { items: [] }
    );
    const resultQuote = result?.quote;
    const resultFullQuotePrice = result?.resultFullQuotePrice ?? undefined;
    const resultItems = result?.items ?? [];
    const resultQuotePresentation = result?.quotePresentation ?? undefined;

    if (resultFullQuotePrice && resultQuote) {
      await postUpdateEvent?.();

      await this.afterItemUpdateProcessing(resultItems, resultFullQuotePrice, resultQuote, resultQuotePresentation);

      return true;
    }
    return false;
  }

  cloneItemContainer(qic: QuoteItemContainer): QuoteItemContainer {
    return {
      buyInData: clone(qic.buyInData),
      data: clone(qic.data),
      item: clone(qic.item),
      price: clone(qic.price)
    };
  }

  /**
   * update a quote item on the server and its thumbnail svg image
   * after update refreshes the quote and quote price object, and all item prices
   * @param quoteItemContainer
   * @param thumbnail
   * @returns the new refreshed copy for the quoteitem
   */
  async saveAndUpdateQuoteItem(
    quoteItemContainer: QuoteItemContainer,
    thumbnail: string,
    extraItems?: InputUpdateQuoteItems,
    thumbnailExtension?: string,
    forceSave?: boolean
  ): Promise<QuoteItemContainer> {
    this._lastSaveSuccessful = false;

    if (!forceSave && this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return quoteItemContainer;
    }

    //if thumbnail is an empty string, then we are not changing anything
    let currentImagePath = '';
    let newImagePath = '';
    if (thumbnail !== '') {
      currentImagePath = quoteItemContainer.item.virtualThumbnailPath ?? '';
      //generate a new image path to refer to when saving the quote item
      newImagePath = this.api.createQuoteItemThumbnailPath(
        this.quoteId,
        quoteItemContainer.item.id,
        thumbnailExtension ?? '.svg'
      );
      quoteItemContainer.item.virtualThumbnailPath = newImagePath;
    }

    this._lastSaveSuccessful = await this.procesItemUpdateBatch(
      quoteItemContainer,
      QuoteItemRowAction.Update,
      extraItems,
      async () => {
        if (thumbnail !== '')
          if (isAutoSaving()) await this.postImageBlob(currentImagePath, newImagePath, thumbnail);
          else this.postImageBlob(currentImagePath, newImagePath, thumbnail);
      }
    );

    return this.quoteItemContainer(quoteItemContainer.item.id);
  }

  public async runAndSaveSSI() {
    if (!isSupplierUsingSSI(this.quote.supplierId)) return;

    const informationDispatcher = new InformationDispatcher();
    const setInfo = async (s: string, header = LangQuoteContainer.preparingToUpdate()) => {
      await informationDispatcher.setInformation(`# ${header}
                + ${s}`);
    };
    await setInfo(LangQuoteContainer.gettingUpdatesFromSupplier());
    const saveModal = new SaveWorkflowModal(LangQuoteContainer.updatingSupplierQuoteItem(), informationDispatcher, 3);

    //we dont want to wait for the modal to finish.
    const modalState = await saveModal.show();
    await modalState.onShow;
    try {
      //process ssi with new information
      //build an ssi processor that will build the quote exluding the deleted item, so that we get data
      //based on its non-existence
      const ssiProcessor = this.ssiProcessor();
      const ssiInputs = await ssiProcessor.processSSI();
      await setInfo(LangQuoteContainer.supplierProcessingComplete());
      if (ssiInputs && ssiInputs.items.length > 0) await this.saveAndUpdateQuoteItems(ssiInputs);
    } finally {
      saveModal?.done();
      await saveModal?.hideModal();
    }
  }

  async saveAndUpdateQuoteItems(extraItems: InputUpdateQuoteItems): Promise<QuoteItemContainer[]> {
    this._lastSaveSuccessful = false;

    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return [];
    }

    this._lastSaveSuccessful = await this.procesItemUpdateBatch(null, QuoteItemRowAction.Update, extraItems);

    const result: QuoteItemContainer[] = [];
    extraItems.items
      .filter(x => x.action !== QuoteItemRowAction.Delete)
      .forEach(element => {
        if (element.input.quoteItem) result.push(this.quoteItemContainer(element.input.quoteItem?.id));
      });
    return result;
  }

  async afterItemUpdateProcessing(
    resultItems: ResultQuoteItem[],
    resultFullQuotePrice: ResultFullQuotePrice,
    resultQuote: Quote,
    resultQuotePresentation?: QuotePresentation
  ) {
    for (let iExtra = 0; iExtra < resultItems.length; iExtra++) await this.processResultItem(resultItems[iExtra]);

    //saving item doesn't change presentation layer, pass in null
    await this.resetQuote(resultQuote, resultFullQuotePrice.quotePrice, resultQuotePresentation ?? null, null);

    if (resultFullQuotePrice?.itemPrices) this.updateItemPrices(resultFullQuotePrice.itemPrices);
    if (resultFullQuotePrice!.itemBuyIns) this.updateItemBuyIns(resultFullQuotePrice.itemBuyIns);

    await this.validateBuyInPrices();
    await this.doAfterSave();
  }

  public async deleteQuoteItem(
    quoteItemContainer: QuoteItemContainer,
    extraItems?: InputUpdateQuoteItems
  ): Promise<boolean> {
    this._lastSaveSuccessful = false;
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return false;
    }

    await this.needsQuoteItems();
    await this.doBeforeDeleteQuoteItem(quoteItemContainer);
    this._lastSaveSuccessful = await this.procesItemUpdateBatch(
      quoteItemContainer,
      QuoteItemRowAction.Delete,
      extraItems
    );

    if (this._lastSaveSuccessful) await this.doAfterDeleteQuoteItem(quoteItemContainer);
    return this._lastSaveSuccessful;
  }
  protected async doBeforeDeleteQuoteItem(_quoteItemContainer: QuoteItemContainer) {
    // do nothing
  }

  removeContainerCache(id: string) {
    let idx = this.itemContainerCache.findIndex(x => x.item.id === id);
    if (idx >= 0) this.itemContainerCache.splice(idx, 1);
    const found = idx >= 0;
    idx = this.itemContainerCacheBak.findIndex(x => x.item.id === id);
    if (idx >= 0) this.itemContainerCacheBak.splice(idx, 1);
    return found;
  }

  async saveAndUpdateQuoteItemWithIndicator(
    quoteItemContainer: QuoteItemContainer,
    thumbnail: string
  ): Promise<QuoteItemContainer> {
    if (!this.changedItem(quoteItemContainer.item.id)) {
      this._lastSaveSuccessful = true;
      return quoteItemContainer;
    }

    let result: QuoteItemContainer = quoteItemContainer;
    this._lastSaveSuccessful = await saveWithIndicator(async () => {
      if (!quoteItemContainer) return false;
      try {
        const newContainer = await this.saveAndUpdateQuoteItem(quoteItemContainer, thumbnail);
        if (newContainer.item.recordVersion !== quoteItemContainer.item.recordVersion) {
          result = newContainer;
          return true;
        }
      } catch {
        return false;
      }
      return false;
    });
    return result;
  }

  async saveAndUpdateQuoteItemPrice(quoteItemPrice: QuoteItemPrice): Promise<QuoteItemContainer> {
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return this.quoteItemContainer(quoteItemPrice.id);
    }

    const updateBuyInItem = (buyInData: string, useBackup: boolean) => {
      const container = useBackup ? this.backup : this.container;
      const idxBuyInData = container.itemsBuyInData?.findIndex(x => x.id == quoteItemPrice.id) ?? -1;

      if (container.itemsBuyInData) {
        if (idxBuyInData > -1 && !buyInData) container.itemsBuyInData?.splice(idxBuyInData, 1);
        else if (idxBuyInData > -1 && buyInData) container.itemsBuyInData[idxBuyInData].buyInData = buyInData;
        else if (idxBuyInData < 0 && buyInData)
          container.itemsBuyInData.push({
            buyInData: buyInData,
            dateCreated: localDateTimeToServer(new Date()),
            id: quoteItemPrice.id,
            recordVersion: ''
          });
      }
    };

    const result = await this.api.updateQuoteItem({
      quoteItemId: quoteItemPrice.id,
      quoteItem: null,
      quoteItemProviderData: null,
      quoteItemBuyInData: null,
      quoteItemPrice: quoteItemPrice
    });
    if (result && result.quoteItemPrice && result.resultFullQuotePrice) {
      if (result.quoteItemBuyInData) {
        const newBuyInData = result.quoteItemBuyInData;
        updateBuyInItem(newBuyInData, false);
        updateBuyInItem(newBuyInData, true);
      }

      await this.afterItemUpdateProcessing([result], result.resultFullQuotePrice, result.quote, undefined);

      const container = this.quoteItemContainer(quoteItemPrice.id);
      fireQuickSuccessToast(LangQuoteContainer.itemPriceUpdated({ title: container?.item.title }));
      return container;
    }

    return this.quoteItemContainer(quoteItemPrice.id);
  }

  /**
   * replaces all item price objects with a fresh set from the server.
   * @param prices a new set of item prices
   */
  updateItemPrices(prices: QuoteItemPrice[]) {
    const update = (list?: QuoteItemPrice[] | null) => {
      list?.forEach(itemPrice => {
        const newPrice = prices.find(x => x.id === itemPrice.id);
        if (newPrice) Object.assign(itemPrice, newPrice);
      });
    };
    update(this.container.itemPrices);
    update(this.backup.itemPrices);
  }

  updateItemBuyIns(buyIns: QuoteItemBuyInData[]) {
    const update = (list?: QuoteItemBuyInData[] | null) => {
      list?.forEach(itemBuyIn => {
        const newBuyIn = buyIns.find(x => x.id === itemBuyIn.id);
        if (newBuyIn) Object.assign(itemBuyIn, newBuyIn);
      });
    };
    update(this.container.itemsBuyInData);
    update(this.backup.itemsBuyInData);
  }

  quoteAddressChanged(): boolean {
    return !compare(this.backup.quote?.shippingAddress, this.container.quote?.shippingAddress);
  }
  quoteChanged(): boolean {
    return (
      !compare(this.backup.quote, this.container.quote) ||
      !compare(this.backup.quoteProviderData, this.container.quoteProviderData) ||
      !compare(this.backup.quotePrice, this.container.quotePrice) ||
      !compare(this.backup.quotePresentation, this.container.quotePresentation)
    );
  }

  changed(): boolean {
    return !compare(this.backup, this.container);
  }

  public async makeCopy(): NullPromise<QuoteContainer> {
    return null;
  }

  public async makeAlternative(_forSupplierReview = false): NullPromise<QuoteContainer> {
    return null;
  }

  public async deleteQuote(): Promise<boolean> {
    return false;
  }

  public async getBuyInCosts(_buyInItems?: QuoteFrameBuyInItem[]): Promise<StockLookupViewExtra[] | undefined> {
    return undefined;
  }

  public lookupBuyInData(
    quoteItemContainer: QuoteItemContainer,
    code: string,
    supplierCode: string,
    lookup: string
  ): StockLookupViewExtra | undefined {
    if (!quoteItemContainer.buyInData) return;

    const buyIns = fromJsonStr<StockLookupViewExtra[]>(quoteItemContainer.buyInData);

    if (!buyIns) return;

    // eslint-disable-next-line consistent-return
    return buyIns.find(x => x.code == code && x.supplierCode == supplierCode && x.lookup == lookup);
  }

  public itemIsNotSortable(item: QuoteItem) {
    return isShipping(item) || isSSI(item) || isDealerQuotePriceAdjustment(item);
  }

  protected async loadSupplierName() {
    if (isEmptyOrSpace(this._supplierName))
      this._supplierName = await getQuoteSupplierDisplayName(this.quote.supplierId);
  }

  protected getItemById(id: string): QuoteItem {
    const item = this.container?.items?.find(x => x.id === id);
    if (!item) throw new DevelopmentError(`${id} missing in list`);
    return item;
  }

  protected async quoteStateBeforeChange(_state: QuoteState, _original: QuoteState): Promise<boolean> {
    return true;
  }

  protected async quoteStateAfterChange(_state: QuoteState, _original: QuoteState): Promise<boolean> {
    return true;
  }

  protected async copyQuoteItemBefore(_quoteItemContainer: QuoteItemContainer): Promise<boolean> {
    return true;
  }

  protected async copyQuoteItemAfter(
    _quoteItemContainer: QuoteItemContainer,
    _newQuoteItemContainer: QuoteItemContainer
  ): Promise<boolean> {
    return true;
  }

  protected async copyQuoteItemFinally(
    _quoteItemContainer: QuoteItemContainer,
    _newQuoteItemContainer: QuoteItemContainer | null
  ): Promise<void> {
    //
  }

  protected async copyQuoteItemExecute(quoteItemContainer: QuoteItemContainer): NullPromise<QuoteItemContainer> {
    const result = await this.api.duplicateQuoteItem({
      quoteItemId: quoteItemContainer.item.id
    });
    if (result) {
      await this.addQuoteItem(
        result.quoteItem,
        result.quoteItemPrice,
        result.quoteItemProviderData,
        result.quoteItemBuyInData?.buyInData ?? null
      );
      await this.resetQuote(result.quote, result.resultFullQuotePrice.quotePrice, result.quotePresentation, null);
      await this.validateBuyInPrices();
      await this.doAfterSave();
      return this.quoteItemContainer(result.quoteItem.id);
    }
    return null;
  }

  protected doAfterDeleteQuoteItem(_quoteItemContainer: QuoteItemContainer) {
    //DoNothing
  }

  protected internalIsReadonly(): boolean {
    return false;
  }

  /**
   * replaces the backups and originals of the quote objects with this new set of objects to become the master
   * @param quote
   * @param quotePrice
   * @param quotePresentation
   */
  protected async resetQuote(
    quote: Quote,
    quotePrice: QuotePrice,
    quotePresentation: QuotePresentation | null,
    siblings: QuoteSetSibling[] | null
  ) {
    if (this.container.quote) Object.assign(this.container.quote, quote);
    else this.container.quote = quote;

    const providerData =
      fromJsonStr<QuoteProviderData>(quote.serviceProviderData) ??
      (await createQuoteProviderData(quote.serviceProvider, quote.supplierId, quote.quoteOwnerId));
    this.container.quoteProviderData = await validateAndUpgradeQuoteProviderData(providerData);
    if (quotePresentation) this.container.quotePresentation = quotePresentation;
    else if (!this.container.quotePresentation) throw new DevelopmentError('Missing QuotePresentation'); //should only occur on updates

    if (this.container.quotePrice) Object.assign(this.container.quotePrice, quotePrice);
    else this.container.quotePrice = quotePrice;

    if (siblings) this.container.siblings = siblings;

    this.backup.quote = this.clone(quote);
    this.backup.quoteProviderData = this.clone(this.container.quoteProviderData);
    this.backup.quotePrice = this.clone(quotePrice);

    //we dont pass in siblings if we didn't update the quote directly
    if (siblings) this.backup.siblings = this.clone(siblings);

    if (quotePresentation) this.backup.quotePresentation = this.clone(quotePresentation);
  }

  /**
   * execute all bound events after any save operation to allow for re-rendering and refreshing of state
   */
  protected async doAfterSave(): Promise<void> {
    await runEventNotify(this.afterSave);
  }

  protected async validateBuyInPricesForItem(id: string) {
    if (this.isBuyInDataLocked) return;
    const buyInData = this.container.itemsBuyInData?.find(x => x.id == id);

    const validationIssueIndex = this.priceValidation?.findIndex(x => x.id == id) ?? -1;

    if (validationIssueIndex > -1) this.priceValidation?.splice(validationIssueIndex, 1);

    if (buyInData) {
      await this.reportBuyInValidation(buyInData);
    }
  }

  protected async validateBuyInPrices() {
    if (this.isBuyInDataLocked) return;
    this.priceValidation = null;
    this.container.itemsBuyInData?.map(async x => {
      await this.reportBuyInValidation(x);
    });
  }

  protected async addQuoteItem(
    quoteItem: QuoteItem,
    quoteItemPrice: QuoteItemPrice,
    quoteItemProviderData: QuoteItemProviderData | null,
    quoteItemBuyInData: string | null
  ) {
    if (this.container.items?.find(x => x.id === quoteItem.id)) {
      await showDevelopmentError(`Quote item ${quoteItem.id} already exists in the quote`);
      return;
    }
    this.container.items?.push(quoteItem);
    if (quoteItemProviderData) this.container.itemsData?.push(quoteItemProviderData);
    if (quoteItemBuyInData)
      this.container.itemsBuyInData?.push({
        id: quoteItem.id,
        buyInData: quoteItemBuyInData,
        dateCreated: localDateTimeToServer(new Date()),
        recordVersion: ''
      });
    if (this.container.itemPrices?.find(x => x.id === quoteItemPrice.id)) {
      await showDevelopmentError(`Quote item Price ${quoteItemPrice.id} already exists in the quote`);
      return;
    }

    this.container.itemPrices?.push(quoteItemPrice);

    this.backup.items?.push(this.clone(quoteItem));
    if (quoteItemProviderData) this.backup.itemsData?.push(this.clone(quoteItemProviderData));
    if (quoteItemBuyInData)
      this.backup.itemsBuyInData?.push({
        id: quoteItem.id,
        buyInData: quoteItemBuyInData,
        dateCreated: localDateTimeToServer(new Date()),
        recordVersion: ''
      });
    this.backup.itemPrices?.push(this.clone(quoteItemPrice));

    const sortedItems = await this.internalSortItemsAsList();
    this.container.items = sortedItems;
    this.backup.items = clone(sortedItems);
  }

  /**
   * perform an insert of text based blob data such as svg on the server
   * @param oldItemPath an old name to delete when uploading
   * @param newItemPath a new name path to use for this insert
   * @param imageData text based data such as svg
   */
  protected async postImageBlob(oldItemPath: string, newItemPath: string, imageData: string | null) {
    if (!imageData) return;
    const base64Index = imageData.indexOf('base64,');
    const data = base64Index >= 0 ? imageData.substring(imageData.indexOf(',') + 1) : btoa(imageData);

    await this.blobApi.updateFileByVirtualPath({
      oldVirtualPath: oldItemPath,
      newVirtualPath: newItemPath,
      data: data
    });
  }

  private getAdjustedPresentationOrder(items: QuoteItem[]): string[] {
    const presentationItems: string[] = [];
    const itemsToMoveToEnd: QuoteItem[] = items.filter(item => this.itemIsNotSortable(item));
    const order = this.container.quotePresentation?.itemDisplayOrder ?? [];

    order.forEach(commonId => {
      const item = items.find(x => x.commonId === commonId);
      if (item && !this.itemIsNotSortable(item)) presentationItems.push(commonId);
    });
    items
      .filter(x => !this.itemIsNotSortable(x) && !presentationItems.includes(x.commonId))
      .forEach(item => {
        presentationItems.push(item.commonId);
      });

    itemsToMoveToEnd.forEach(item => {
      if (!isShipping(item) && isSSI(item)) presentationItems.push(item.commonId);
    });
    itemsToMoveToEnd.forEach(item => {
      if (isShipping(item)) presentationItems.push(item.commonId);
    });
    itemsToMoveToEnd.forEach(item => {
      if (isDealerQuotePriceAdjustment(item)) presentationItems.push(item.commonId);
    });
    return presentationItems;
  }

  private async internalSortItemsAsList(items?: QuoteItem[]) {
    const sortingItems = items ?? this.container.items ?? [];
    if (!this.container.quotePresentation) {
      await showDevelopmentError(LangQuoteContainer.quotePresentationMissing()); // Localized
      return sortingItems;
    }
    const sortedItems: QuoteItem[] = [];
    const displayOrder = this.getAdjustedPresentationOrder(sortingItems);
    this.container.quotePresentation.itemDisplayOrder = displayOrder;

    //create a list ordered by the quote presentation
    displayOrder.forEach(commonId => {
      const item = sortingItems.find(item => item.commonId === commonId);
      if (item) {
        sortedItems.push(item);
      }
    });
    if (sortedItems.length !== sortingItems.length) {
      sortedItems.push(...sortingItems.filter(item => !displayOrder.includes(item.commonId)));
    }
    return sortedItems;
  }

  private updateItemContainer(updatedItemContainer: QuoteItemContainer, useBackup: boolean) {
    const container = useBackup ? this.backup : this.container;
    const idxItem = container.items?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1;

    let idxData = -1;
    if (updatedItemContainer.data)
      idxData = container.itemsData?.findIndex(x => x.id === updatedItemContainer.data?.id) ?? -1;

    let idxBuyInData = -1;
    if (updatedItemContainer.buyInData)
      idxBuyInData = container.itemsBuyInData?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1;

    const idxPrice = container.itemPrices?.findIndex(x => x.id === updatedItemContainer.price.id) ?? -1;

    if (idxItem >= 0 && container.items)
      container.items[idxItem] = useBackup ? this.clone(updatedItemContainer.item) : updatedItemContainer.item;
    if (idxData >= 0 && container.itemsData && updatedItemContainer.data)
      container.itemsData[idxData] = useBackup ? this.clone(updatedItemContainer.data) : updatedItemContainer.data;
    if (idxPrice >= 0 && container.itemPrices)
      container.itemPrices[idxPrice] = useBackup ? this.clone(updatedItemContainer.price) : updatedItemContainer.price;

    if (container.itemsBuyInData) {
      if (idxBuyInData > -1 && !updatedItemContainer.buyInData) container.itemsBuyInData?.splice(idxBuyInData, 1);
      else if (idxBuyInData > -1 && updatedItemContainer.buyInData)
        container.itemsBuyInData[idxBuyInData].buyInData = updatedItemContainer.buyInData;
      else if (idxBuyInData < 0 && updatedItemContainer.buyInData)
        container.itemsBuyInData.push({
          buyInData: updatedItemContainer.buyInData,
          dateCreated: localDateTimeToServer(new Date()),
          id: updatedItemContainer.item.id,
          recordVersion: ''
        });
    }
  }

  private async reportBuyInValidation(itemBuyInData: QuoteItemBuyInData) {
    if (this.isBuyInDataLocked) return;

    const metaDataForItem = fromJsonStr<StockLookupViewExtra[]>(itemBuyInData.buyInData);

    if (metaDataForItem) {
      const buyins = await this.getBuyInCosts(
        metaDataForItem.map(y => {
          return {
            code: y.code,
            partCode: '',
            extraDetails: {
              LibCode: y.libCode,
              SuppCode: y.supplierCode,
              SupplierId: y.supplierId
            },
            quantity: 1,
            resourceType: 0,
            decription: ''
          };
        })
      );

      //Once V6 returns the libcode, we need to filter on that as well
      // eslint-disable-next-line array-callback-return
      metaDataForItem.map(y => {
        const serverVersion = buyins?.find(
          z => z.code == y.code && y.supplierCode == z.supplierCode && y.supplierId == z.supplierId
        );

        // Check for missing costs
        if (y.cost == null || !serverVersion) {
          this.addPriceValidation(itemBuyInData.id, y.code, 'Missing cost price');
        }

        //Check for catalogue updates updates
        if (y.version !== serverVersion?.version) {
          this.addPriceValidation(itemBuyInData.id, y.code, 'Buy-in catalogue has changed');
        }
      });
    }
  }

  private internalDeleteQuoteItemReference(quoteItemId: string) {
    function remove(list: { id: string }[]) {
      const idx = list.findIndex(x => x.id === quoteItemId) ?? -1;
      if (idx >= 0) list.splice(idx, 1);
    }

    if (this.container.items) remove(this.container.items);
    if (this.container.itemPrices) remove(this.container.itemPrices);
    if (this.container.itemsData) remove(this.container.itemsData);
    if (this.container.itemsBuyInData) remove(this.container.itemsBuyInData);

    if (this.backup.items) remove(this.backup.items);
    if (this.backup.itemPrices) remove(this.backup.itemPrices);
    if (this.backup.itemsData) remove(this.backup.itemsData);
    if (this.backup.itemsBuyInData) remove(this.backup.itemsBuyInData);
    this.removeContainerCache(quoteItemId);
  }

  /**
   * find and replace the data elements of a quoteItem with a new refreshed set of data
   * uses a clone of the data passed in
   * @param updatedItemContainer
   * @param useBackup
   */
  private replaceQuoteItem(updatedItemContainer: QuoteItemContainer, useBackup: boolean) {
    const container = useBackup ? this.backup : this.container;
    const idxItem = container.items?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1;

    let idxData = -1;
    if (updatedItemContainer.data)
      idxData = container.itemsData?.findIndex(x => x.id === updatedItemContainer.data?.id) ?? -1;

    const idxPrice = container.itemPrices?.findIndex(x => x.id == updatedItemContainer.price.id) ?? -1;
    if (idxItem >= 0 && container.items) container.items[idxItem] = this.clone(updatedItemContainer.item);
    if (updatedItemContainer.data && idxData >= 0 && container.itemsData)
      container.itemsData[idxData] = this.clone(updatedItemContainer.data);

    let idxBuyInData = -1;
    if (updatedItemContainer.buyInData)
      idxBuyInData = container.itemsBuyInData?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1;

    if (container.itemsBuyInData) {
      if (idxBuyInData > -1 && !updatedItemContainer.buyInData) container.itemsBuyInData?.splice(idxBuyInData, 1);
      else if (idxBuyInData > -1 && updatedItemContainer.buyInData)
        container.itemsBuyInData[idxBuyInData].buyInData = updatedItemContainer.buyInData;
      else if (idxBuyInData < 0 && updatedItemContainer.buyInData)
        container.itemsBuyInData.push({
          buyInData: updatedItemContainer.buyInData,
          dateCreated: localDateTimeToServer(new Date()),
          id: updatedItemContainer.item.id,
          recordVersion: ''
        });
    }

    if (idxPrice >= 0 && container.itemPrices) container.itemPrices[idxPrice] = this.clone(updatedItemContainer.price);
  }

  private hasItemById(id: string) {
    return this.quoteItems.find(x => x.id === id) !== undefined;
  }

  public saveQuoteInlineDocuments(
    docs: InlineDocument[] | undefined | null,
    target: {
      inlineDocuments: InlineDocument[] | null;
    },
    origin: string = 'ssi'
  ): boolean {
    function isSame(doc1: InlineDocument, doc2: InlineDocument): boolean {
      return doc1.origin === doc2.origin && doc1.contentType === doc2.contentType && doc1.name === doc2.name;
    }

    let changed = false;

    if (!target.inlineDocuments && !docs) return false;

    // Ensure target.inlineDocuments is initialized
    if (!target.inlineDocuments) target.inlineDocuments = [];

    // Add or update documents
    docs?.forEach(doc => {
      const activeDoc = target.inlineDocuments!.find(x => isSame(x, doc));
      if (activeDoc) {
        if (activeDoc.content !== doc.content) {
          activeDoc.content = doc.content;
          changed = true;
        }
      } else {
        target.inlineDocuments!.push(doc);
        changed = true;
      }
    });

    // Remove documents with the given origin not present in docs
    const beforeLength = target.inlineDocuments.length;
    target.inlineDocuments = target.inlineDocuments.filter(x => x.origin !== origin || docs?.some(y => isSame(x, y)));
    if (target.inlineDocuments.length !== beforeLength) changed = true;

    return changed;
  }
}
